/**
 * The two decisions behind the Travel Acknowledgments card, extracted so they
 * can be tested without loading the card.
 *
 * ⚠️ IT IS SEPARATE BECAUSE IMPORTING THE CARD PULLS IN MSAL. The card imports
 * `@/lib/apiClient`, which imports `lib/msalConfig`, which reads
 * `window.location.origin` AT MODULE SCOPE. `apps/web` runs vitest in a node
 * environment with no DOM, so a spec that statically imports anything on that
 * chain dies on `ReferenceError: window is not defined` before a single
 * assertion runs. (CI on #542. It is not a hypothetical.)
 *
 * The repo's existing way round that is to stub `globalThis.window` in a
 * `beforeAll` and then `await import()` the component — see
 * `ManpowerRequestCard.spec.tsx`. That is the right tool when a spec genuinely
 * needs to render a component. Neither function below needs React, a query
 * client, a DOM or an API, so the honest fix is for them not to be behind that
 * import in the first place.
 *
 * ⚠️ SO DO NOT MOVE THESE BACK INTO THE CARD. They look small enough to live
 * beside their only caller, and doing that breaks their spec in a way whose
 * error message names `msalConfig` and not this decision.
 */

/**
 * The public signing URL for a token, on whatever host the scheduler is
 * already on.
 *
 * ⚠️ Takes the origin rather than reading `window.location`, for the same
 * reason this module exists: a pure function is testable anywhere.
 */
export function acknowledgmentUrl(origin: string, token: string): string {
  return `${origin.replace(/\/$/, '')}/travel-acknowledgment/${token}`;
}

/**
 * The shape this module needs from an acknowledgment row, and no more.
 *
 * ⚠️ DECLARED HERE RATHER THAN IMPORTED from `@/lib/apiClient`. A type-only
 * import is erased at compile time and would be safe — but it puts the
 * msalConfig chain back in this file's import list, where the next person
 * refactoring an `import type` into a value import reintroduces the crash
 * above. Two fields are not worth that.
 */
export interface IssuedAcknowledgment {
  rosterId: number;
  signedAt: string | null;
  /** A newer link exists for this person, so this one no longer works. */
  replaced?: boolean;
}

/**
 * Whether issuing another link for this person needs a confirmation.
 *
 * ⚠️ AN UNSIGNED LINK IS THE CASE THAT MATTERS, not simply "any existing row".
 * Once somebody has signed, a new link is the normal way to re-issue against a
 * changed band, and asking about it every time trains people to click through
 * the one prompt that counts. An unsigned one is the double-click, and the
 * second link is what leaves a person holding two forms with no way to tell
 * which is real.
 *
 * The API cannot make this call: re-issuing is legitimate, so it has no
 * grounds to refuse. The guard belongs on the screen.
 *
 * ⚠️ A REPLACED LINK IS NOT AN OPEN ONE. Re-issuing makes the older unsigned
 * link stop working (the API's `replaced`, Codex P1 on #542), so once it has
 * been replaced there is nothing left for a new link to collide with. Counting
 * it would ask the question again on every issue after the first re-issue.
 */
export function needsReissueConfirmation(
  rows: IssuedAcknowledgment[],
  rosterId: number,
): boolean {
  return rows.some((r) => r.rosterId === rosterId && r.signedAt == null && !r.replaced);
}

/**
 * Whether the Issue button may be pressed at all.
 *
 * ⚠️⚠️ EVERY FIELD HERE EXISTS BECAUSE THE DUPLICATE-LINK GUARD FAILED OPEN,
 * AND IT FAILED OPEN TWICE. (Codex, P2 twice on #542.) Both times the shape
 * was identical: `needsReissueConfirmation` was asked its question against
 * rows that were not known to be current, and an empty or stale list answers
 * "no existing link, go ahead".
 *
 *   listLoaded    The query had not come back yet, or had FAILED, and the card
 *                 read its rows as `list.data ?? []`. Two queries feed that
 *                 screen and resolve independently — the roster fills the
 *                 dropdown, the list fills the guard — so this was not a
 *                 narrow race but the ordinary case of one request being
 *                 faster than another.
 *
 *   listFetching  The FIRST fix missed this one. After a successful issue the
 *                 card invalidates the list, which starts a background
 *                 refetch; during it react-query keeps serving the PREVIOUS
 *                 data, so `isSuccess` stays true while the rows are stale and
 *                 `issuing` has already gone false. Re-select the same person
 *                 in that window and the guard consults a list that does not
 *                 contain the row just created.
 *
 * ⚠️ SO THE RULE IS NOT "the query settled" — IT IS "these rows are current".
 * Anything short of that has to refuse, because a guard that fails open is
 * worse than no guard: the screen looks like it is protecting you. The caller
 * checks this again inside the click handler, since a disabled button is a
 * rendering and not an invariant.
 */
export function canIssueLink(state: {
  /** The select's value. Empty string means nobody is chosen. */
  rosterId: string;
  /** A request is already in flight. */
  issuing: boolean;
  /** The existing-acknowledgments query has SUCCEEDED — not merely settled. */
  listLoaded: boolean;
  /**
   * A fetch is in flight for that query — initial or background. While one is,
   * react-query serves the data from BEFORE it, so the rows on screen are not
   * known to be current.
   */
  listFetching: boolean;
}): boolean {
  return state.rosterId !== ''
    && !state.issuing
    && state.listLoaded
    && !state.listFetching;
}

/**
 * Every row of a paged list — not the first page of it.
 *
 * ⚠️ THE BUG THIS REPLACES: the card fetched the travel roster as one request
 * with `limit: 500`, and 500 is the API's MAX_PAGE_SIZE. Past 500 active people,
 * everybody after the first page — alphabetically, the end of the roster —
 * was silently absent from the picker, so no link could be issued to them even
 * though the API would accept their roster ids. (Codex, P2 on #542.) The
 * roster is far smaller than that today; the failure would have arrived with
 * no error, on the day it stopped being.
 *
 * Stops on the server's own `total`, or on an empty page — the empty page is
 * the only guard against a `total` that disagrees with what the pages return,
 * which would otherwise loop.
 *
 * ⚠️ THROWS AT THE CEILING RATHER THAN RETURNING WHAT IT HAS. A partial list
 * handed back quietly is precisely the defect being fixed. Thrown, it surfaces
 * through the card's roster error banner instead.
 */
export async function collectAllPages<T>(
  fetchPage: (offset: number) => Promise<{ rows: T[]; total: number }>,
  maxPages = 50,
): Promise<T[]> {
  const all: T[] = [];
  for (let page = 0; page < maxPages; page += 1) {
    const { rows, total } = await fetchPage(all.length);
    all.push(...rows);
    if (rows.length === 0 || all.length >= total) return all;
  }
  throw new Error(`The list has more than ${maxPages} pages, so it could not all be loaded.`);
}

/** The SharePoint subfolder signed acknowledgments are filed into, under the Job Folder. */
export const FILING_SUBFOLDER = 'Travel Acknowledgments';

/**
 * What a row's filing control is: nothing (not signed), the filed link, the
 * button, or why the button is not there.
 *
 * ⚠️ `folder` IS THREE-VALUED ON PURPOSE. `undefined` = this card does not
 * know whether the job has a SharePoint folder; `null` = it knows there is
 * none. Collapsing the two would tell a scheduler "no folder linked" during a
 * load — the same unfounded claim the empty-list states on this card were
 * fixed for.
 *
 * ⚠️ AND "DOES NOT KNOW" IS EITHER LOADING OR FAILED, which `folderFailed`
 * tells apart. Without it a failed project lookup read as loading forever: the
 * button stayed disabled with nothing on screen saying why or what to do.
 * (Codex, P2 on #559.) The caller passes a folder whenever it has project data,
 * even from before a failed refetch, so a failure only shows when there is
 * genuinely nothing to go on.
 */
export function filingAction(
  row: { signedAt: string | null; filedUrl: string | null; pdfFileName: string | null },
  folder: string | null | undefined,
  folderFailed = false,
): 'none' | 'filed' | 'file' | 'no-folder' | 'loading' | 'folder-error' {
  if (!row.signedAt || !row.pdfFileName) return 'none';
  if (row.filedUrl) return 'filed';
  if (folder === undefined) return folderFailed ? 'folder-error' : 'loading';
  if (!folder) return 'no-folder';
  return 'file';
}
